import React, { useEffect, useState } from 'react';
import styles from '../../style.module.scss';
import { getImagePath, getPLPThumbnailPath } from '@/app/lib/converters/convertAdditionalImages';
import { DeviceType, IProduct } from '@mybestbrands/types';
import { trackSSTEvent } from '@/app/utils/tracking';
import CouponLabel from '@/app/ui/components/list/listitem/listitemcouponlabel';
import ColorLabel from '@/app/ui/components/list/listitem/colorsLabel';
import MultipleImagesDesktop from '@/app/ui/components/list/listitem/listitemmultipleimages/multipleimagesdesktop';
import MultipleImagesMobile from '@/app/ui/components/list/listitem/listitemmultipleimages/multipleimagesmobile';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { appSettingsState } from '@/app/lib/redux';
import { handleWishList } from '@/app/helpers/wishlist';
import CouponCountDownLabel from '@/app/ui/components/list/listitem/couponcountdownlabel';
import { FeatureToggles } from '@/app/featuretoggles/ft';

interface Props {
  item: IProduct;
  locale: string;
  trackingData?: object | null;
  onCouponClick?: () => void;
  groupProducts?: IProduct[];
  isRelatedProduct?: boolean;
  deviceType?: DeviceType;
  ft?: FeatureToggles;
  onWishListRemove?: () => void;
  apiUrl?: string;
}

const ListItemMultipleImages = ({
  item,
  locale,
  trackingData,
  onCouponClick,
  groupProducts,
  isRelatedProduct,
  deviceType,
  ft,
  onWishListRemove,
  apiUrl,
}: Props) => {
  const thumbnails = item.additionalImages.map((i) => getImagePath(i, locale, item.retailerId, item.productId, true)); // fetch xxl images as we don't have them in small_portrait format yet
  const mainImage = getPLPThumbnailPath(item.mainImageUrl, locale, item.retailerId, item.productId, 'xxl'); // fetch large image
  const isRelated = isRelatedProduct && deviceType !== DeviceType.DESKTOP; // for related products on mobile do not show multiple images
  const images = Array.from(new Set(isRelated ? [mainImage] : [mainImage, ...thumbnails]));
  const colorVariantItems = groupProducts?.filter((el) => !/(\..*){2,}/.test(el.productId || ''));
  const discountInPercent =
    item.originalPrice && item.price ? Math.round((1 - item.price / item.originalPrice) * 100) : 0;
  const hasCouponAmount = !!item.priorityCoupon?.amount;

  const { user } = useSelector(appSettingsState);
  const dispatch = useDispatch();
  const [isInWishlist, setIsInWishlist] = useState(false);

  useEffect(() => {
    if (user?.WatchlistProducts) {
      const isWishlistItem = user.WatchlistProducts.some((wishlistProduct) => wishlistProduct === item.productId);
      setIsInWishlist(isWishlistItem);
    }
  }, [user?.WatchlistProducts, item.productId]);

  const onWishListClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleWishList(user, isInWishlist, item.productId, dispatch, apiUrl, trackingData);
    if (isInWishlist && onWishListRemove) {
      onWishListRemove();
    }
  };

  return (
    <div
      onClick={() => trackSSTEvent('product_card_image_click', trackingData || null)}
      className={'d-flex justify-content-center align-items-center mb-3 position-relative'}>
      {!!item.priorityCoupon?.end_date_utc && (
        <CouponCountDownLabel endTime={item.priorityCoupon?.end_date_utc} trackingData={trackingData} />
      )}
      {item.hasCoupon && (item.freeShippingCoupon || hasCouponAmount) && (
        <CouponLabel item={item} onClick={onCouponClick} blackFridayToggle={ft?.BLACK_FRIDAY} />
      )}
      {discountInPercent > 0 && <div className={styles.discountLabel}>-{discountInPercent}%</div>}
      {!ft?.HIDE_PROFILE_LINKS && locale === 'de' && (
        <div className={styles['wishlistHeart']} onClick={onWishListClick}>
          <Image
            src={`/intstatic/icons/${isInWishlist ? 'icon-heart-filled' : 'icon-heart-outline'}.svg`}
            alt={'Heart Icon'}
            width={15}
            height={15}
          />
        </div>
      )}
      {colorVariantItems && colorVariantItems.length > 1 && item.colors.length >= 1 && (
        <ColorLabel colorVariantItems={colorVariantItems} />
      )}
      {deviceType === DeviceType.DESKTOP ? (
        <MultipleImagesDesktop
          images={images}
          item={item}
          isRelatedProduct={isRelatedProduct}
          trackingData={trackingData}
        />
      ) : (
        <MultipleImagesMobile images={images} item={item} />
      )}
    </div>
  );
};

export default ListItemMultipleImages;
